import React from 'react';
import {
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Switch as MuiSwitch,
  SwitchProps,
  FormHelperText,
  Box
} from '@mui/material';
import clsx from 'clsx';

import { CheckboxProps } from '../../interfaces';
import { FormContext, FormRefsContext } from '../../contexts';
import { FormMode } from '../../enums';
import { useFormContext, useGetFieldRules, useValidateInput } from '../../hooks';

interface BooleanComponentProps extends CheckboxProps {
  useSwitch: boolean;
}

export function BooleanField({ name, ...props }: BooleanComponentProps): JSX.Element {
  const contextRules = useGetFieldRules<BooleanComponentProps>(name);
  return <BooleanComponent {...{ ...contextRules, ...props }} name={name} />;
}

function BooleanComponent(props: BooleanComponentProps): JSX.Element {
  const [value, setValue] = React.useState(false);
  const { formMode } = React.useContext(FormContext);
  const { fieldsRefs } = React.useContext(FormRefsContext);
  const { setValueByName } = useFormContext();

  const updateValue = (value: boolean): void => {
    setValue(value);
    setValueByName(props.name, value);
    if (props.onChange) {
      props.onChange(value);
    }
  };

  const { error } = useValidateInput(props);

  const handleChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    updateValue(checked);
  };

  React.useEffect(() => {
    updateValue(!!props.defaultValue);
  }, []);

  React.useEffect(() => {
    if (props.value != null && props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  const booleanProps = (): SwitchProps => ({
    name: props.name,
    checked: value,
    onChange: handleChange,
    color: 'primary',
    size: props.size,
    ref: (el: HTMLButtonElement) => {
      if (fieldsRefs && props?.name) {
        fieldsRefs.current[props.name] = el;
      }
    }
  });

  return (
    <Box sx={{ display: 'inline-block' }}>
      <FormControlLabel
        className={clsx(
          {
            'form-field': true,
            'required-submit': props.requiredToSubmit
          },
          props.className
        )}
        id={props.id}
        control={
          props.useSwitch ? <MuiSwitch {...booleanProps()} /> : <MuiCheckbox {...booleanProps()} />
        }
        label={props.customLabel ?? props.label}
        disabled={props.disabled || formMode === FormMode.ReadOnly}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
}
